import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Modal, Typography } from '@mui/material'
import { StoryEditor, buildStoryAskPrompt, useAgentChat, useFileAttachments, webappEntryRelPath } from '../../agent'
import type { ArtifactInfo, StoryAskTarget, UseStoryDraftResult } from '../../agent'
import { API_BASE_URL } from '../../constants/system'
import { buildTranscribeEndpoint } from '../../hooks/useTranscribeEndpoint'
import { findStoryArtifact } from '../../utils/storyBoardEditing'
import usePersistedFlag from '../../hooks/usePersistedFlag'
import StoryAgentPanel, { STORY_AGENT_PANEL_WIDTH } from './StoryAgentPanel'
import StoryEditorExplainer, { explainerSeenKey } from './StoryEditorExplainer'
import { storyPanelRows } from './storyPanelRows'

// The story board editor, full screen (#1416; Adam, 27 Sep: "you go into edit
// mode, and it's literally full screen, everything"). It covers the whole
// window, the app's header and nav included, and gives the story page the
// full width. The agent is in a panel that slides in from the right
// (StoryAgentPanel), with the session's conversation cut down to the last few
// turns.
//
// It is a layer, not a page: the session page stays mounted under it, with
// its message list hidden (display: none, set by the page) so nothing there
// is laid out or painted while the editor is open. The chat provider, its
// state and its stream are untouched, so a message sent from the panel is
// the session's own, and closing puts the reader back where they were.
//
// Page → hook → component: the page owns useStoryDraft (every request) and
// hands its result in as `draft`. This reads the chat context for the
// artifact, the conversation and the send.

interface StoryEditorScreenProps {
  sessionId: string
  artifactId: string
  draft: UseStoryDraftResult
  authHeader?: string
  /** Why this person may not send to the agent, or undefined. */
  composerDisabledReason?: string
  onClose: () => void
  /** The signed-in reader's email, for the explainer's per-person "seen" flag. */
  userEmail?: string
  /** Publish a story board that is not in DataViz yet: the page's publish dialog (#1578). */
  onPublishNew?: (artifact: ArtifactInfo) => void
}

const ASK_CHIP_ID = 'story-element'
const WORKING_NOTICE = 'The agent is working. Ask again when it finishes.'

export default function StoryEditorScreen({ sessionId, artifactId, draft, authHeader, composerDisabledReason, onClose, userEmail, onPublishNew }: StoryEditorScreenProps) {
  const { session, artifacts, isStreaming, messages, activityStatus, sendMessage, selectedModel, setSelectedModel, config } = useAgentChat()
  const [confirmClose, setConfirmClose] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [askTarget, setAskTarget] = useState<StoryAskTarget | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const artifact = findStoryArtifact(artifacts, artifactId)
  const attach = useFileAttachments({ apiBaseUrl: API_BASE_URL, authHeader })

  // The five-step walkthrough (#1578 item 10): shown once per person on
  // their first open, reopenable from the bar's help icon at any time.
  const [explainerSeen, setExplainerSeen] = usePersistedFlag(explainerSeenKey(userEmail || ''), false)
  const [explainerOpen, setExplainerOpen] = useState(false)
  const explainerAutoShown = useRef(false)
  // Only once the editor itself is actually on screen: while the draft is
  // still loading StoryEditor shows nothing
  // but a spinner, so the bar's testids the explainer anchors to don't exist
  // yet. Opening it earlier left it permanently blank (#1578 blocker).
  const editorReady = draft.status === 'ready'
  useEffect(() => {
    if (explainerAutoShown.current || explainerSeen || !editorReady) return
    explainerAutoShown.current = true
    setExplainerOpen(true)
  }, [explainerSeen, editorReady])
  const closeExplainer = () => {
    setExplainerOpen(false)
    setExplainerSeen(true)
  }
  // When a turn ends, the agent may have rewritten the page: files it wrote
  // under the story dir (not just story.html) are copied to blob storage on
  // the way out (#1578 item 9), so the frame's own cached skin/lib/data must
  // be refetched too — a plain reload of the draft source is not enough.
  const [frameRev, setFrameRev] = useState(0)
  const wasStreaming = useRef(isStreaming)
  useEffect(() => {
    const was = wasStreaming.current
    wasStreaming.current = isStreaming
    if (was && !isStreaming) {
      setFrameRev((n) => n + 1)
      // keepUnsaved: edits typed during the fetch, or already on screen, are
      // never thrown away. When they exist AND the agent's turn moved the
      // saved base, `reload` surfaces that as the same conflict a stale save
      // would — the "Load newest" banner — instead of silently doing
      // nothing while the reader keeps editing a page that has moved on.
      if (draft.status === 'ready') void draft.reload({ keepUnsaved: true })
      setNotice(null)
    }
    // Triggered by the turn ending only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming])

  // The model the main composer would send with: a session created before its
  // model was retired falls back to the configured default (as AgentChat does).
  const models = config.models
  const model = models.length > 0 && !models.some(m => m.id === selectedModel)
    ? (models.some(m => m.id === config.defaultModel) ? config.defaultModel ?? '' : models[0]?.id ?? '')
    : selectedModel

  // Every entry point opens the panel: the bar's "Ask the agent", "Ask the
  // agent about this" on a selection, "Change via agent" on a locked figure.
  // An element becomes the chip in the panel's composer; the reader types
  // what should change. What they have already typed there is never touched.
  const handleAskAgent = (target: StoryAskTarget | null) => {
    setPanelOpen(true)
    if (target) setAskTarget(target)
    // A message sent while a turn runs is queued behind it, and the page it
    // quotes may have changed by the time it runs; refuse instead.
    setNotice(isStreaming ? WORKING_NOTICE : null)
  }

  // The panel's Send. Saves unsaved edits first, so the agent reads what is on
  // screen. Returns false when nothing was sent: the composer then keeps the
  // reader's words.
  const handleSend = async (text: string): Promise<boolean> => {
    if (isStreaming) {
      setNotice(WORKING_NOTICE)
      return false
    }
    if (draft.dirty) {
      const saved = await draft.save()
      if (!saved) {
        setNotice('Your edits could not be saved, so nothing was sent. The editor says why.')
        return false
      }
    }
    setNotice(null)
    const content = askTarget ? buildStoryAskPrompt(askTarget.excerpt, text, askTarget.label) : text
    setAskTarget(null)
    const ids = await attach.uploadAll(sessionId)
    attach.clear()
    void sendMessage(content, model, ids.length ? ids : undefined)
    return true
  }

  const handleClose = () => {
    if (draft.dirty) setConfirmClose(true)
    else onClose()
  }

  // The link named a story board from another session: nothing here will load it.
  const elsewhere = draft.status === 'ready' && !!draft.sessionId && draft.sessionId !== sessionId

  let body: React.ReactNode
  if (!artifact && elsewhere) {
    body = (
      <Box data-testid="story-editor-elsewhere" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, p: 3 }}>
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>This story board is not in this session.</Typography>
        <Button color="info" size="small" onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </Box>
    )
  } else if (!artifact) {
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, p: 3 }}>
        {draft.status === 'error' ? (
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>This story board could not be opened for editing: {draft.loadError}</Typography>
        ) : (
          <>
            <CircularProgress size={26} color="info" />
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Opening the story board…</Typography>
          </>
        )}
        <Button color="info" size="small" onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </Box>
    )
  } else {
    // The app's own index.html, same-origin, with the token in the path so its
    // relative lib/ and data/ requests carry it (as ArtifactPreviewDialog does).
    // Not the story's `entry` (story.html): the runtime loads ./lib/story.js
    // relative to the frame.
    const token = authHeader?.replace(/^Bearer\s+/, '')
    const entry = webappEntryRelPath(artifact)
    const base = token
      ? `${API_BASE_URL}/webapp/${sessionId}/${token}/${entry}`
      : `${API_BASE_URL}/agent/session/${sessionId}/workspace/files/${entry}`
    // Cache-bust on every turn end (frameRev) so the frame refetches
    // skin/lib/data instead of serving what the browser cached before the
    // agent's files were copied over; the first mount carries no suffix.
    const frameSrc = frameRev ? `${base}?rev=${frameRev}` : base
    body = (
      <StoryEditor
        frameSrc={frameSrc}
        draft={draft}
        title={artifact.label || 'Story board'}
        onAskAgent={handleAskAgent}
        askOpen={panelOpen}
        asideWidth={STORY_AGENT_PANEL_WIDTH}
        onClose={handleClose}
        onPublishNew={onPublishNew ? () => onPublishNew(artifact) : undefined}
        notices={isStreaming && !panelOpen ? (
          <Typography data-testid="story-editor-agent-working" sx={{ px: 2, py: 0.75, fontSize: 12, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider' }}>
            The agent is working. When it finishes, its changes load here.
          </Typography>
        ) : undefined}
        aside={(
          <StoryAgentPanel
            open={panelOpen}
            onClose={() => setPanelOpen(false)}
            rows={storyPanelRows(messages)}
            working={isStreaming}
            workingLabel={activityStatus?.label}
            onSend={handleSend}
            models={models}
            selectedModel={model}
            onModelChange={setSelectedModel}
            chips={askTarget ? [{ id: ASK_CHIP_ID, label: askTarget.label, tooltip: askTarget.excerpt }] : []}
            onRemoveChip={() => setAskTarget(null)}
            notice={notice}
            onDismissNotice={() => setNotice(null)}
            disabledReason={composerDisabledReason}
            onOpenFullConversation={handleClose}
            attachments={attach.attachments}
            onFilesSelected={attach.addFiles}
            onRemoveAttachment={attach.removeFile}
            // The mic posts to goapi's /transcribe, which refuses a call with
            // no customer (security audit C23): name the session's.
            transcribeEndpoint={buildTranscribeEndpoint(session?.customer, session?.job)}
            authHeader={authHeader}
          />
        )}
        onHelp={() => setExplainerOpen(true)}
      />
    )
  }

  return (
    // A Modal, so the app underneath is out of the tab order and hidden from
    // assistive technology while the editor is open, and the editor's own
    // dialogs and menus stack above it. No backdrop: the layer is opaque.
    // Escape does not close it: Escape inside the page ends an edit.
    <Modal open hideBackdrop disableEscapeKeyDown>
      <Box
        data-testid="story-editor-screen"
        role="dialog"
        aria-modal="true"
        aria-label="Story board editor"
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          outline: 'none',
          // The layer never scrolls: the page scrolls inside its frame.
          overflow: 'hidden',
          '@supports (overflow: clip)': { overflow: 'clip' },
          '@keyframes storyEditorIn': {
            from: { opacity: 0, transform: 'translateY(6px)' },
            to: { opacity: 1, transform: 'none' },
          },
          animation: 'storyEditorIn 180ms cubic-bezier(0.2, 0, 0, 1)',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {body}
        <StoryEditorExplainer open={explainerOpen} onClose={closeExplainer} />
        <Dialog open={confirmClose} onClose={() => setConfirmClose(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Close without saving?</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              Your changes since the last save will be lost.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="info" onClick={() => setConfirmClose(false)} sx={{ textTransform: 'none' }}>Keep editing</Button>
            <Button
              color="info"
              variant="outlined"
              onClick={() => { setConfirmClose(false); onClose() }}
              sx={{ textTransform: 'none' }}
            >
              Discard changes
            </Button>
            <Button
              color="info"
              variant="contained"
              disableElevation
              onClick={async () => {
                const ok = await draft.save()
                setConfirmClose(false)
                if (ok) onClose()
              }}
              sx={{ textTransform: 'none' }}
            >
              Save and close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Modal>
  )
}
